import { Link } from '@tanstack/react-router'
import { ChevronDown } from 'lucide-react'
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { PortalNavigationItem } from '@/shared/schemas/brand.schema'
import { portalNavAriaCurrentPage } from '@/shared/lib/portalNavAriaCurrent'
import { cn } from '@/shared/lib/utils'

type PortalMenuItemsProps = {
  items: PortalNavigationItem[]
  pathname: string
  variant: 'header' | 'sheet'
  linkClassName: string
  onNavigate?: () => void
  srExternal: string
}

export function PortalMenuItems({
  items,
  pathname,
  variant,
  linkClassName,
  onNavigate,
  srExternal,
}: PortalMenuItemsProps) {
  const { t } = useTranslation()
  const rootRef = useRef<HTMLUListElement>(null)
  const submenuRefs = useRef<Record<string, HTMLUListElement | null>>({})
  const [menuState, setMenuState] = useState<{ pathname: string; openId: string | null }>({ pathname, openId: null })
  const openId = menuState.pathname === pathname ? menuState.openId : null
  const setOpenId = (nextOpenId: string | null) => setMenuState({ pathname, openId: nextOpenId })
  const idPrefix = useId().replaceAll(':', '')

  useEffect(() => {
    const closeOutside = (event: PointerEvent) => {
      if (rootRef.current !== null && !rootRef.current.contains(event.target as Node)) {
        setMenuState({ pathname, openId: null })
      }
    }
    document.addEventListener('pointerdown', closeOutside)
    return () => document.removeEventListener('pointerdown', closeOutside)
  }, [pathname])

  const navigate = () => {
    setOpenId(null)
    onNavigate?.()
  }

  const renderLink = (
    item: PortalNavigationItem,
    className: string,
    onKeyDown?: (event: KeyboardEvent<HTMLAnchorElement>) => void,
  ): ReactNode => {
    const title = item.link_title?.trim() !== '' ? item.link_title : undefined
    if (item.link_type === 'internal') {
      return (
        <Link
          to={item.href}
          title={title}
          target={item.target?.trim() || undefined}
          className={className}
          aria-current={portalNavAriaCurrentPage(pathname, item.href) ? 'page' : undefined}
          onClick={navigate}
          onKeyDown={onKeyDown}
        >
          {item.label}
        </Link>
      )
    }
    if (item.link_type === 'external') {
      const target = item.target?.trim() !== '' ? item.target : '_blank'
      const rel = item.rel?.trim() !== '' ? item.rel : target === '_blank' ? 'noopener noreferrer' : undefined
      return (
        <a
          href={item.href}
          title={title}
          target={target}
          rel={rel}
          className={className}
          onClick={navigate}
          onKeyDown={onKeyDown}
        >
          {item.label}
          <span className="sr-only">{srExternal}</span>
        </a>
      )
    }
    return null
  }

  const focusSubmenu = (itemId: string, last = false) => {
    requestAnimationFrame(() => {
      const links = submenuRefs.current[itemId]?.querySelectorAll<HTMLElement>('a[href]')
      const target = links?.[last ? links.length - 1 : 0]
      target?.focus()
    })
  }

  const handleToggleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, itemId: string) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      setOpenId(itemId)
      focusSubmenu(itemId, event.key === 'ArrowUp')
    } else if (event.key === 'Escape') {
      event.preventDefault()
      setOpenId(null)
    }
  }

  const handleSubmenuLinkKeyDown = (event: KeyboardEvent<HTMLAnchorElement>, itemId: string) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      setOpenId(null)
      document.getElementById(`${idPrefix}-${itemId}-toggle`)?.focus()
      return
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return

    const links = [...(event.currentTarget.closest('ul')?.querySelectorAll<HTMLElement>('a[href]') ?? [])]
    if (links.length === 0) return

    event.preventDefault()
    const step = event.key === 'ArrowDown' ? 1 : -1
    links[(links.indexOf(event.currentTarget) + step + links.length) % links.length]?.focus()
  }

  return (
    <ul ref={rootRef} className={variant === 'header' ? 'flex items-center gap-1' : 'flex flex-col gap-1'}>
      {items.map((item) => {
        const hasChildren = item.children.length > 0
        if (!hasChildren) {
          return <li key={item.id}>{renderLink(item, linkClassName)}</li>
        }

        const expanded = openId === item.id
        const submenuId = `${idPrefix}-${item.id}-submenu`
        const toggle = (
          <button
            id={`${idPrefix}-${item.id}-toggle`}
            type="button"
            className={cn(linkClassName, item.link_type !== 'group' && 'px-2')}
            aria-expanded={expanded}
            aria-controls={submenuId}
            aria-label={item.link_type === 'group' ? undefined : t('app.layout.submenu_toggle', { label: item.label })}
            onClick={() => setOpenId(expanded ? null : item.id)}
            onKeyDown={(event) => handleToggleKeyDown(event, item.id)}
          >
            {item.link_type === 'group' ? item.label : null}
            <ChevronDown className={cn('size-4 transition-transform', expanded && 'rotate-180')} aria-hidden />
          </button>
        )

        return (
          <li key={item.id} className={cn('relative', variant === 'sheet' && 'w-full')}>
            {item.link_type === 'group' ? (
              toggle
            ) : (
              <div className="flex items-center">
                {renderLink(item, cn(linkClassName, 'flex-1'))}
                {toggle}
              </div>
            )}
            <ul
              id={submenuId}
              ref={(element) => { submenuRefs.current[item.id] = element }}
              hidden={!expanded}
              className={cn(
                'z-50 min-w-56 rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md',
                variant === 'header' ? 'absolute left-0 top-full mt-1' : 'ml-4 mt-1',
              )}
            >
              {item.children.map((child) => (
                <li key={child.id}>
                  {renderLink(child, cn(linkClassName, 'block w-full'), (event) => handleSubmenuLinkKeyDown(event, item.id))}
                </li>
              ))}
            </ul>
          </li>
        )
      })}
    </ul>
  )
}
